<script lang="ts">
  import { session, currentStep, records, selectedChecklistId } from '../stores';
  import { checklists } from '../data/checklists';
  import type { InspectionRecord } from '../types';
  import { ROLE_LABELS } from '../types';

  const userSession = $derived($session);
  const checklistId = $derived($selectedChecklistId);
  const currentChecklist = $derived(
    checklists.find(c => c.id === checklistId)
  );

  const currentRecords = $derived($records);

  const completedCount = $derived(
    currentChecklist ? Object.values(currentRecords).filter(r => r.status === 'ok').length : 0
  );

  const totalCount = $derived(currentChecklist?.items.length || 0);
  const progress = $derived(totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0);

  function toggleItem(itemId: string) {
    records.update(prev => {
      const newRecords = { ...prev };
      if (newRecords[itemId]) {
        delete newRecords[itemId];
      } else {
        newRecords[itemId] = {
          itemId,
          status: 'ok',
          timestamp: new Date().toISOString()
        };
      }
      return newRecords;
    });
  }

  function handleFinish() {
    // 檢查必填項
    const unfinished = currentChecklist?.items.filter(
      item => item.required && !currentRecords[item.id]
    ) || [];

    if (unfinished.length > 0) {
      alert(`尚有 ${unfinished.length} 個必填項目未勾選，請完成後再提交！`);
      return;
    }

    currentStep.set('summary');
  }

  function handleBack() {
    if (confirm('返回將清除您目前的檢查進度，確定要返回嗎？')) {
      session.set(null);
      records.set({});
      selectedChecklistId.set(null);
      currentStep.set('selection');
    }
  }

  // 管委可留言（儲存在 localStorage，鍵名依 checklist id）
  import { onMount } from 'svelte';
  let committeeComment = $state('');
  let currentChecklistId: string | null = null;

  onMount(() => {
    const unsub = selectedChecklistId.subscribe(id => {
      currentChecklistId = id;
      if (id && typeof localStorage !== 'undefined') {
        const key = `committee_comment_${id}`;
        committeeComment = localStorage.getItem(key) ?? '';
      } else {
        committeeComment = '';
      }
    });
    return () => unsub();
  });

  function saveCommitteeComment() {
    if (!currentChecklistId) return;
    const key = `committee_comment_${currentChecklistId}`;
    localStorage.setItem(key, committeeComment || '');
    alert('意見已儲存');
  }
</script>

<div class="w-full">
  {#if currentChecklist && userSession}
    <div class="mb-6 sm:mb-8">
      <div class="flex justify-between items-start mb-4">
        <div>
          <h2 class="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">
            {currentChecklist.title}
          </h2>
          <p class="text-xl sm:text-2xl text-blue-700 font-semibold">
            {userSession.name || ROLE_LABELS[userSession.role]}
          </p>

          {#if userSession.role === 'committee'}
            <div class="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
              <label for="committee-comment" class="block text-sm sm:text-base font-semibold text-yellow-800 mb-2">管委意見（可選）</label>
              <textarea id="committee-comment" bind:value={committeeComment} rows="3" class="w-full p-3 rounded-md border-2 border-yellow-200"></textarea>
              <div class="mt-2 text-right">
                <button onclick={saveCommitteeComment} class="px-4 py-2 bg-yellow-600 text-white rounded-lg font-bold">送出意見</button>
              </div>
            </div>
          {/if}
        </div>
        <button
          onclick={handleBack}
          class="px-3 sm:px-4 py-2 sm:py-3 bg-red-500 hover:bg-red-600 text-white font-bold rounded-lg text-lg sm:text-xl"
        >
          返回
        </button>
      </div>

      <!-- 進度條 -->
      <div class="mb-6 p-4 bg-gray-100 rounded-2xl">
        <div class="flex justify-between items-center mb-2">
          <span class="text-lg sm:text-xl font-bold text-gray-700">進度</span>
          <span class="text-xl sm:text-2xl font-bold text-blue-700">
            {completedCount}/{totalCount}
          </span>
        </div>
        <div class="w-full bg-gray-300 rounded-full h-8 overflow-hidden">
          <div
            class="bg-linear-to-r from-green-400 to-green-500 h-full rounded-full transition-all duration-300 flex items-center justify-center"
            style="width: {progress}%"
          >
            {#if progress > 10}
              <span class="text-white font-bold text-sm sm:text-base">{progress}%</span>
            {/if}
          </div>
        </div>
      </div>
    </div>

    <!-- 檢查項目列表 -->
    <div class="space-y-3 mb-8">
      {#each currentChecklist.items as item, idx (item.id)}
        <button
          onclick={() => toggleItem(item.id)}
          class="w-full text-left p-5 sm:p-6 rounded-2xl border-3 transition-all active:scale-95 flex items-start gap-4"
          class:bg-green-50={currentRecords[item.id]}
          class:border-green-500={currentRecords[item.id]}
          class:bg-white={!currentRecords[item.id]}
          class:border-gray-300={!currentRecords[item.id]}
        >
          <div class="mt-1 shrink-0">
            {#if currentRecords[item.id]}
              <div class="w-10 h-10 sm:w-12 sm:h-12 bg-green-500 rounded-full flex items-center justify-center shrink-0">
                <span class="text-white text-xl sm:text-2xl">✓</span>
              </div>
            {:else}
              <div class="w-10 h-10 sm:w-12 sm:h-12 border-4 border-gray-400 rounded-full"></div>
            {/if}
          </div>

          <div class="grow">
            <div class="flex items-baseline gap-2">
              <span class="text-gray-500 text-lg sm:text-xl font-bold">
                {idx + 1}.
              </span>
              <p
                class="text-xl sm:text-2xl font-bold"
                class:text-green-700={currentRecords[item.id]}
                class:text-gray-800={!currentRecords[item.id]}
              >
                {item.text}
                {#if item.required}
                  <span class="text-red-600 ml-2">*</span>
                {/if}
              </p>
            </div>
            {#if item.hint}
              <p class="text-sm sm:text-base text-blue-700 mt-2 font-semibold">
                📸 {item.hint}
              </p>
            {/if}
          </div>
        </button>
      {/each}
    </div>

    <!-- 提交按鈕 -->
    <div class="sticky bottom-4 sm:bottom-6">
      <button
        onclick={handleFinish}
        class="w-full bg-linear-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white p-6 sm:p-8 rounded-2xl text-2xl sm:text-3xl font-bold shadow-xl active:scale-95 transition-all"
      >
        ✓ 完成檢查清單
      </button>
    </div>
  {/if}
</div>
